export const MAX_ATTEMPTS = 3;
export const REQUEST_TIMEOUT_MS = 30_000;
export const BASE_BACKOFF_MS = 500;
export const BACKOFF_CAP_MS = 8_000;

const RETRIABLE_STATUSES = new Set<number>([0, 408, 429, 502, 503, 504]);
const RETRIABLE_METHODS = new Set<string>(['GET', 'HEAD']);

export function isRetriableStatus(status: number): boolean {
  return RETRIABLE_STATUSES.has(status);
}

export function isRetriableMethod(method: string): boolean {
  return RETRIABLE_METHODS.has((method || '').toUpperCase());
}

/**
 * Full-jittered exponential backoff. `attempt` is 0-based (0 = first retry).
 * delay = random(0, min(cap, base * 2^attempt))
 */
export function computeBackoffMs(
  attempt: number,
  base: number = BASE_BACKOFF_MS,
  cap: number = BACKOFF_CAP_MS,
  rand: () => number = Math.random
): number {
  const expWindow = Math.min(cap, base * Math.pow(2, Math.max(0, attempt)));
  return Math.floor(rand() * expWindow);
}

/**
 * Parses an HTTP `Retry-After` header into a delay in milliseconds.
 * Supports both the `delta-seconds` form (e.g. `120`) and the HTTP-date form
 * (e.g. `Wed, 21 Oct 2026 07:28:00 GMT`).
 *
 * Returns `null` when the header is missing, empty, or malformed (negative
 * delta-seconds, fractional seconds, or any value that isn't a recognized
 * HTTP-date). Returns `0` (retry immediately) when an HTTP-date is in the
 * past — per RFC 7231 the value is advisory and a past date is treated as
 * "no further wait required."
 */
/**
 * Strips the query string from a URL. Used before exporting request URLs to
 * telemetry — query strings frequently carry sensitive data (signed tokens,
 * customer identifiers, search terms) that should not leave the app via RUM.
 */
export function stripQuery(url: string): string {
  const i = url.indexOf('?');
  return i === -1 ? url : url.slice(0, i);
}

export function parseRetryAfterMs(header: string | null | undefined, now: number): number | null {
  if (header == null) return null;
  const trimmed = String(header).trim();
  if (trimmed === '') return null;

  if (/^\d+$/.test(trimmed)) {
    const seconds = Number(trimmed);
    if (!Number.isFinite(seconds) || seconds < 0) return null;
    return Math.floor(seconds * 1000);
  }

  // Reject anything that isn't an HTTP-date — must contain a letter (e.g. "Wed", "GMT").
  // Date.parse is otherwise permissive enough to accept "-5", "5.5", etc. as historical years.
  if (!/[a-zA-Z]/.test(trimmed)) return null;

  const dateMs = Date.parse(trimmed);
  if (Number.isNaN(dateMs)) return null;
  const delta = dateMs - now;
  return delta > 0 ? delta : 0;
}
